import React, { useState, useRef } from 'react';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getPlayerNameColor, getDisplayElo, formatEloForUi, playerMatchesSearchQuery } from '../../utils/playerUtils';
import { Button, Modal, ModalContent, ModalCloseButton, MobileSearchField, PlayerPickerSheet } from '../common';
import { isMobileSearchSheet } from '../../utils/mobileSearchInput';
import useKeyboardAwareScrollMaxHeight from '../../hooks/useKeyboardAwareScrollMaxHeight';

/** Full-row tap target shared by the desktop modal list and the mobile PlayerPickerSheet. */
function PlayerSelectionRow({ player, useSinglesElo, onSelect, showEloTooltip = true }) {
  const playerName = `${player.first_name || ''} ${player.last_name || ''}`.trim();
  const nameFormat = formatNameForDisplay(playerName, { maxLength: 25, useInitials: false });

  return (
    <button
      type="button"
      onClick={() => onSelect(player.id)}
      className="w-full p-3 min-h-[44px] touch-manipulation text-left bg-surface-subtle hover:bg-primary-light border border-border-medium rounded-lg transition-colors text-text-primary"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className={`font-medium ${getPlayerNameColor(player.gender)}`}>{nameFormat.display}</span>
        </div>
        {showEloTooltip ? (
        <div className="text-sm text-text-secondary">
          Elo: {formatEloForUi(getDisplayElo(player, null, useSinglesElo))}
        </div>
        ) : null}
      </div>
    </button>
  );
}

/** Picks a player for an empty court slot. Mobile opens a PlayerPickerSheet; desktop keeps the centered modal. */
const PlayerSelectionModal = ({
  isOpen,
  onClose,
  availablePlayers,
  onSelectPlayer,
  courtNumber,
  teamNumber,
  useSinglesElo = false,
  showEloTooltip = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileSheet] = useState(() => isMobileSearchSheet());
  const searchAnchorRef = useRef(null);
  const listMaxHeight = useKeyboardAwareScrollMaxHeight(searchAnchorRef, { enabled: isOpen && !isMobileSheet });

  if (!isOpen) return null;

  const filteredPlayers = availablePlayers.filter(
    (player) => player && playerMatchesSearchQuery(player, searchTerm)
  );

  const handlePlayerSelect = (playerId) => {
    onSelectPlayer(playerId, courtNumber, teamNumber);
    onClose();
  };

  const emptyMessage = searchTerm ? 'No available players found — try adjusting your search' : 'No available players found';

  const rows = (
    <div className="space-y-2">
      {filteredPlayers.map(
        (player) =>
          player && (
            <PlayerSelectionRow
              key={player.id}
              player={player}
              useSinglesElo={useSinglesElo}
              showEloTooltip={showEloTooltip}
              onSelect={handlePlayerSelect}
            />
          )
      )}
    </div>
  );

  if (isMobileSheet) {
    return (
      <PlayerPickerSheet
        isOpen={isOpen}
        onClose={onClose}
        title={`Select player — Court ${courtNumber}, team ${teamNumber}`}
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search players…"
        hasResults={filteredPlayers.length > 0}
        emptyMessage={emptyMessage}
      >
        {rows}
      </PlayerPickerSheet>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="small" className="max-h-[min(90vh,640px)] overflow-hidden flex flex-col">
        <ModalCloseButton onClose={onClose} />
        <h3 className="text-lg font-semibold text-text-primary mb-4 pr-8">
          Select Player for Court {courtNumber} - Team {teamNumber}
        </h3>

        <div ref={searchAnchorRef} className="mb-4 shrink-0">
          <MobileSearchField
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
        >
          {filteredPlayers.length === 0 ? (
            <div className="text-center text-text-secondary py-8">
              <p>No available players found</p>
              {searchTerm && <p className="text-sm mt-2">Try adjusting your search</p>}
            </div>
          ) : (
            rows
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-border-medium shrink-0">
          <Button onClick={onClose} variant="secondary" className="w-full">
            Cancel
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default PlayerSelectionModal;
